import * as React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { DATE_PRESETS, activeDatePreset, dateRangeLabel, todayISO, type DateRange } from 'lib/dateRange';
import { DatePicker } from './date-picker';
import { FilterChip } from './filter-button';
import { Popover, PopoverContent } from './popover';

interface DateRangeFilterProps {
  label: string;
  value: DateRange;
  onChange: (range: DateRange) => void;
}

/** A date filter on the filter line: quick "since" presets, or a custom from/to range. */
export function DateRangeFilter({ label, value, onChange }: DateRangeFilterProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const preset = activeDatePreset(value);
  const valueLabel = dateRangeLabel(value, t, i18n.language);

  const clear = () => onChange({ from: undefined, to: undefined });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <FilterChip label={label} valueLabel={valueLabel} onClear={clear} />
      <PopoverContent
        align="start"
        className="w-72 p-1.5"
        // The date pickers open their calendars in their own popper portals.
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) event.preventDefault();
        }}
      >
        <div role="listbox" aria-label={label}>
          {DATE_PRESETS.map(({ key, from }) => {
            const selected = preset === key;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(selected ? { from: undefined, to: undefined } : { from: from(), to: undefined });
                  setOpen(false);
                }}
                className={cn(
                  'flex h-[34px] w-full items-center gap-2 rounded-md px-2 text-left text-[13.5px] text-foreground transition-colors hover:bg-muted',
                  selected && 'font-semibold',
                )}
              >
                <span className="flex-1">{t(key)}</span>
                {selected && <Check className="h-4 w-4" strokeWidth={2.25} aria-hidden />}
              </button>
            );
          })}
        </div>
        <div className="-mx-1.5 my-1.5 h-px bg-border" />
        <div className="space-y-1.5 px-1 pb-1">
          <p className="text-xs font-medium text-muted-foreground">{t('filter_custom_range')}</p>
          <div className="grid grid-cols-2 gap-2">
            <DatePicker
              value={value.from}
              max={value.to || todayISO()}
              onChange={(v) => onChange({ from: v || undefined, to: value.to })}
              placeholder={t('date_from')}
              ariaLabel={t('date_from')}
            />
            <DatePicker
              value={value.to}
              min={value.from}
              max={todayISO()}
              onChange={(v) => onChange({ from: value.from, to: v || undefined })}
              placeholder={t('date_to')}
              ariaLabel={t('date_to')}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangeFilter;
